/*
 * File: strict-input.ts
 * File Created: 2024-01-11 06:43:50
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-04-26 03:18:55
 * Modified By: Val Liu
 * -----
 */

import { VNode } from "vue";
import { Vue } from "vue-property-decorator";
import { DirectiveBinding, DirectiveFunction, DirectiveOptions } from "vue/types/options";
import { getBinding, getChildInput } from "./core";

interface IStrictInput {
    enabled?: boolean;
    regex?: RegExp;
    value?: string;
    "update:value"?: (value: string) => void;
}

const defaultOptions = {
    enabled: true,
};

const LAST_VALUE = Symbol(`Last Value`);
const INITIAL = Symbol(`Initial`);
const LISTENER = Symbol(`Input Listener`);

/// usage
// v-strict-input="{
//     ... IStrictInput ...
// }"

Vue.directive("strict-input", {
    componentUpdated(el, binding, vnode) {
        return componentUpdated.apply(this, arguments as any);
    },

    bind(el, binding, vnode) {
        return bind.apply(this, arguments as any);
    },
    unbind(el) {
        return unbind.apply(this, arguments as any);
    },
});

function emitUpdateValue(
    input: HTMLInputElement,
    update: Function | undefined,
    value: string | null,
) {
    input[LAST_VALUE] = value;
    if (update && typeof update === "function") {
        update(value);
    }
}

function componentUpdated(
    this: DirectiveOptions | DirectiveFunction,
    el: HTMLElement,
    binding: DirectiveBinding,
    vnode: VNode,
) {
    const {
        enabled,
        regex,
        "update:value": update,
    } = getBinding<IStrictInput>(binding, defaultOptions);
    const input = getChildInput(el);
    if (!input) return;

    let value: string | null = binding.value?.value;
    const oldValue: string | null = binding.oldValue?.value;
    if (value === oldValue) return;

    // validate with regex
    if (!regex || value == null || regex.test(value)) {
        /// do nothing
    } else {
        /// if not match, fallback to null
        value = null;
    }

    function execute(input) {
        input.value = value;
        emitUpdateValue(input, update, value);
    }
    /// if initialed, add a short delay.
    let init = input[INITIAL];
    if (init) {
        input[INITIAL] = false;
        setTimeout(() => execute(input), 0);
    } else {
        execute(input);
    }
}

function bind(
    this: DirectiveOptions | DirectiveFunction,
    el: HTMLElement,
    binding: DirectiveBinding,
    vnode: VNode,
) {
    const {
        enabled,
        regex,
        "update:value": update,
    } = getBinding<IStrictInput>(binding, defaultOptions);

    const input = getChildInput(el);
    if (!input) return;

    const inputListener = () => {
        if (enabled === false || regex == undefined) return;

        // validate with regex
        if (!regex.test(input.value)) {
            input.value = input[LAST_VALUE]; // Revert to last valid value if the argument is 'validate'
        } else {
            emitUpdateValue(input, update, input.value);
        }
    };

    input[LISTENER] = inputListener;
    input.addEventListener("input", inputListener);

    /// initial binding
    input[INITIAL] = true;
    componentUpdated.apply(this as any, arguments as any);
}

function unbind(
    this: DirectiveOptions | DirectiveFunction,
    el: HTMLElement,
    binding: DirectiveBinding,
    vnode: VNode,
) {
    const input = getChildInput(el);
    if (!input) return;

    if (input[LISTENER]) {
        input.removeEventListener("input", input[LISTENER]);
        delete input[LISTENER];
    }
}
